'use client';

import { useEffect, useState } from 'react';
import { MarketingHeader } from '@/components/marketing/marketing-header';
import { MarketingFooter } from '@/components/marketing/marketing-footer';
import { MarketingPageHero } from '@/components/marketing/marketing-page-hero';
import { MotionSection } from '@/components/marketing/motion-section';
import { checkAuthenticationStatus } from '@/features/auth/actions/auth-actions';

const sections = [
  { id: 'parties', title: '1. Parties & Scope' },
  { id: 'definitions', title: '2. Definitions' },
  { id: 'roles', title: '3. Roles & Responsibilities' },
  { id: 'ferpa', title: '4. FERPA — School Official' },
  { id: 'gdpr', title: '5. GDPR Obligations' },
  { id: 'australian-privacy', title: '6. Australian Privacy Act' },
  { id: 'data-security', title: '7. Data Security' },
  { id: 'sub-processors', title: '8. Sub-Processors' },
  { id: 'data-subject-rights', title: '9. Data Subject Rights' },
  { id: 'data-return', title: '10. Data Return & Deletion' },
  { id: 'breach-notification', title: '11. Breach Notification' },
  { id: 'audit', title: '12. Audit Rights' },
  { id: 'term', title: '13. Term & Termination' },
  { id: 'contact', title: '14. Contact' }
];

export default function DpaPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    checkAuthenticationStatus()
      .then((result) => {
        if (isMounted) {
          setIsAuthenticated(result.isAuthenticated);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <MarketingHeader
        isAuthenticated={isAuthenticated}
        isLoading={isLoading}
      />

      <MarketingPageHero
        badge='Legal'
        title='Data Processing Agreement'
        description='Version 1.0.0 | Last updated: 24 February 2026'
      />

      <section className='py-16'>
        <div className='container'>
          <div className='grid gap-10 lg:grid-cols-12'>
            <aside className='hidden lg:col-span-3 lg:block'>
              <MotionSection>
                <nav className='sticky top-24'>
                  <p
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'var(--mktg-gray)',
                      marginBottom: '16px'
                    }}
                  >
                    On This Page
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {sections.map((section) => (
                      <li key={section.id}>
                        <a href={`#${section.id}`} className='mktg-toc-link'>
                          {section.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </MotionSection>
            </aside>

            <div className='mktg-prose lg:col-span-9'>
              <MotionSection>
                <div
                  style={{
                    background: 'var(--mktg-neutral, #f9fafb)',
                    border: '1px solid var(--mktg-card-border, #e5e7eb)',
                    borderRadius: '12px',
                    padding: '20px 24px',
                    marginBottom: '32px'
                  }}
                >
                  <p style={{ margin: 0 }}>
                    <strong>Note:</strong> This page summarises the key
                    provisions of the TEX Data Processing Agreement. To execute
                    a DPA for your school or organisation, contact{' '}
                    <a
                      href='mailto:privacy@texedu.app'
                      className='text-primary hover:underline'
                    >
                      privacy@texedu.app
                    </a>
                    . The executed agreement will incorporate these provisions
                    and any school-specific schedules.
                  </p>
                </div>
              </MotionSection>

              <MotionSection>
                <section id='parties' className='scroll-mt-24'>
                  <h2>1. Parties &amp; Scope</h2>
                  <p>
                    This Data Processing Agreement (&quot;DPA&quot;) is entered
                    into between <strong>TEX Inc Pty Ltd</strong>{' '}
                    (&quot;TEX,&quot; &quot;Processor&quot;) and the subscribing
                    school or educational organisation (&quot;School,&quot;
                    &quot;Controller&quot;) and supplements the Terms of Service
                    and subscription agreement between the parties.
                  </p>
                  <p>
                    This DPA governs the processing of personal data that TEX
                    performs on behalf of the School in connection with the
                    provision of the TEX Edu. It applies to all personal data
                    processed by TEX on behalf of the School, including student
                    education records, staff data, and any other personal
                    information submitted to the Platform.
                  </p>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='definitions' className='scroll-mt-24'>
                  <h2>2. Definitions</h2>
                  <p>
                    In addition to terms defined in the Terms of Service, the
                    following definitions apply:
                  </p>
                  <ul>
                    <li>
                      <strong>&quot;Personal Data&quot;</strong> means any
                      information relating to an identified or identifiable
                      natural person, including education records as defined
                      under FERPA and personal information as defined under the
                      Australian Privacy Act 1988.
                    </li>
                    <li>
                      <strong>&quot;Education Records&quot;</strong> means
                      records directly related to a student and maintained by
                      the School or by TEX acting for the School, as defined in
                      FERPA (20 U.S.C. &sect; 1232g).
                    </li>
                    <li>
                      <strong>&quot;Processing&quot;</strong> means any
                      operation performed on Personal Data, including
                      collection, recording, storage, retrieval, use,
                      disclosure, and deletion.
                    </li>
                    <li>
                      <strong>&quot;Data Breach&quot;</strong> means any
                      unauthorised access, acquisition, use, or disclosure of
                      Personal Data that compromises the security,
                      confidentiality, or integrity of such data.
                    </li>
                    <li>
                      <strong>&quot;Sub-Processor&quot;</strong> means any third
                      party engaged by TEX to process Personal Data on behalf of
                      the School.
                    </li>
                  </ul>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='roles' className='scroll-mt-24'>
                  <h2>3. Roles &amp; Responsibilities</h2>
                  <p>
                    The <strong>School</strong> is the data controller (or,
                    under FERPA, the educational agency or institution) and
                    determines the purposes and means of processing Personal
                    Data. The School is responsible for ensuring that all
                    necessary consents, notices, and authorisations are in place
                    for the lawful processing of Personal Data.
                  </p>
                  <p>
                    <strong>TEX</strong> is the data processor and processes
                    Personal Data only on behalf of and under the documented
                    instructions of the School. TEX shall not process Personal
                    Data for any purpose other than to provide the services
                    described in the subscription agreement.
                  </p>
                  <p>TEX shall:</p>
                  <ul>
                    <li>
                      Process Personal Data only in accordance with the
                      School&apos;s documented instructions.
                    </li>
                    <li>
                      Ensure that personnel authorised to process Personal Data
                      are bound by obligations of confidentiality.
                    </li>
                    <li>
                      Implement and maintain appropriate technical and
                      organisational security measures.
                    </li>
                    <li>
                      Assist the School in responding to data subject requests
                      and regulatory inquiries.
                    </li>
                    <li>
                      Not sell, rent, trade, or otherwise make available
                      Personal Data to any third party, except as necessary to
                      provide the services or as required by law.
                    </li>
                    <li>
                      Not use Personal Data for marketing, advertising, or AI
                      model training purposes.
                    </li>
                  </ul>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='ferpa' className='scroll-mt-24'>
                  <h2>4. FERPA — School Official Designation</h2>
                  <p>
                    This section applies to Schools subject to the{' '}
                    <strong>
                      Family Educational Rights and Privacy Act (FERPA), 20
                      U.S.C. &sect; 1232g
                    </strong>
                    .
                  </p>
                  <p>
                    The School designates TEX as a &quot;school official&quot;
                    with a &quot;legitimate educational interest&quot; under
                    FERPA &sect;99.31(a)(1)(i)(B). This designation is based on
                    the following criteria:
                  </p>
                  <ul>
                    <li>
                      <strong>Institutional function:</strong> TEX performs
                      services that the School would otherwise use its own
                      employees to perform, including student career readiness
                      management, opportunity tracking, skills assessment
                      administration, and counselor workflow support.
                    </li>
                    <li>
                      <strong>Direct control:</strong> The School retains direct
                      control over TEX&apos;s use and maintenance of Education
                      Records through this DPA, the subscription agreement, and
                      the School&apos;s administrative controls within the
                      Platform.
                    </li>
                    <li>
                      <strong>Purpose limitation:</strong> TEX uses Education
                      Records solely for the authorised purposes specified in
                      this DPA and the subscription agreement.
                    </li>
                    <li>
                      <strong>Re-disclosure restrictions:</strong> TEX is
                      subject to FERPA &sect;99.33(a) re-disclosure restrictions
                      and shall not re-disclose personally identifiable
                      information from Education Records to any third party
                      except as authorised by the School or permitted under
                      FERPA.
                    </li>
                  </ul>

                  <h3>Annual Notification Support</h3>
                  <p>
                    TEX will provide the School with the information necessary
                    for the School to include TEX in its annual FERPA
                    notification to parents regarding the School&apos;s criteria
                    for determining who constitutes a school official and what
                    constitutes a legitimate educational interest.
                  </p>

                  <h3>Directory Information</h3>
                  <p>
                    The Platform supports the School in managing directory
                    information designations and opt-out preferences. TEX will
                    not treat any student information as directory information
                    unless the School has designated it as such within the
                    Platform&apos;s privacy settings.
                  </p>

                  <h3>Parental Rights</h3>
                  <p>
                    TEX will provide reasonable assistance to the School in
                    fulfilling its obligations to parents and eligible students
                    under FERPA, including the right to inspect and review
                    Education Records, the right to request amendment, and the
                    right to consent to disclosures. The School is responsible
                    for receiving and processing such requests directly.
                  </p>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='gdpr' className='scroll-mt-24'>
                  <h2>5. GDPR Obligations</h2>
                  <p>
                    Where the General Data Protection Regulation (EU) 2016/679
                    or UK GDPR applies, the following provisions supplement the
                    general obligations of this DPA:
                  </p>
                  <ul>
                    <li>
                      <strong>Lawful basis:</strong> TEX processes Personal Data
                      on behalf of the School under the lawful basis of
                      contractual necessity (Article 6(1)(b)) and legitimate
                      interest (Article 6(1)(f)).
                    </li>
                    <li>
                      <strong>Data Protection Impact Assessment:</strong> TEX
                      will assist the School in conducting DPIAs where required
                      and maintain its own DPIA for the Platform.
                    </li>
                    <li>
                      <strong>Cross-border transfers:</strong> Where Personal
                      Data is transferred outside the EEA/UK, TEX relies on
                      Standard Contractual Clauses (SCCs) or equivalent
                      safeguards as approved by the relevant supervisory
                      authority.
                    </li>
                    <li>
                      <strong>Data subject rights:</strong> TEX will assist the
                      School in responding to data subject access requests
                      (DSARs) within the timeframes required by GDPR.
                    </li>
                  </ul>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='australian-privacy' className='scroll-mt-24'>
                  <h2>6. Australian Privacy Act</h2>
                  <p>
                    TEX complies with the{' '}
                    <strong>Privacy Act 1988 (Cth)</strong> and the{' '}
                    <strong>Australian Privacy Principles (APPs)</strong>. Where
                    the School is an Australian entity:
                  </p>
                  <ul>
                    <li>
                      TEX processes Personal Data in accordance with APPs 1–13.
                    </li>
                    <li>
                      Overseas disclosure of Personal Data (APP 8) is limited to
                      the sub-processors listed in Section 8 and is subject to
                      contractual safeguards requiring equivalent data
                      protection.
                    </li>
                    <li>
                      TEX will notify the School and the Office of the
                      Australian Information Commissioner (OAIC) of eligible
                      data breaches in accordance with the Notifiable Data
                      Breaches (NDB) scheme under Part IIIC of the Privacy Act.
                    </li>
                  </ul>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='data-security' className='scroll-mt-24'>
                  <h2>7. Data Security</h2>
                  <p>
                    TEX implements and maintains technical and organisational
                    measures appropriate to the nature, scope, and purposes of
                    processing, including:
                  </p>
                  <ul>
                    <li>
                      <strong>Encryption:</strong> AES-256 encryption at rest;
                      TLS 1.3 for all data in transit.
                    </li>
                    <li>
                      <strong>Access controls:</strong> Role-based access
                      control (RBAC) with 14 distinct roles; principle of least
                      privilege enforced for all personnel.
                    </li>
                    <li>
                      <strong>Multi-tenant isolation:</strong> Row-level
                      security (RLS) policies ensure complete data isolation
                      between school tenants.
                    </li>
                    <li>
                      <strong>Authentication:</strong> Secure authentication via
                      Supabase Auth with support for SSO (Google, Microsoft
                      Azure AD) and multi-factor authentication.
                    </li>
                    <li>
                      <strong>Audit logging:</strong> Comprehensive audit trails
                      for data access and modifications.
                    </li>
                    <li>
                      <strong>Infrastructure:</strong> Hosted on Vercel and
                      Supabase (AWS-backed), both SOC 2 Type II compliant. Data
                      stored in the Sydney, Australia region by default.
                    </li>
                    <li>
                      <strong>Vulnerability management:</strong> Regular
                      dependency updates, automated security scanning, and
                      third-party penetration testing.
                    </li>
                  </ul>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='sub-processors' className='scroll-mt-24'>
                  <h2>8. Sub-Processors</h2>
                  <p>
                    TEX uses the following sub-processors to provide the
                    Platform services. Each sub-processor is contractually bound
                    to data protection obligations equivalent to those in this
                    DPA:
                  </p>
                  <div className='overflow-x-auto'>
                    <table>
                      <thead>
                        <tr>
                          <th>Sub-Processor</th>
                          <th>Purpose</th>
                          <th>Data Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Supabase (AWS)</td>
                          <td>Database hosting &amp; authentication</td>
                          <td>Sydney, AU</td>
                        </tr>
                        <tr>
                          <td>Vercel</td>
                          <td>Application hosting &amp; file storage</td>
                          <td>Sydney, AU (edge)</td>
                        </tr>
                        <tr>
                          <td>OpenAI</td>
                          <td>
                            AI features (no model training on school data)
                          </td>
                          <td>United States</td>
                        </tr>
                        <tr>
                          <td>Resend</td>
                          <td>Transactional email delivery</td>
                          <td>United States</td>
                        </tr>
                        <tr>
                          <td>Sentry</td>
                          <td>Error monitoring (anonymised)</td>
                          <td>United States</td>
                        </tr>
                        <tr>
                          <td>PostHog</td>
                          <td>Product analytics (anonymised)</td>
                          <td>EU</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>
                    TEX will provide the School with at least 30 days&apos;
                    prior written notice before engaging any new sub-processor
                    or making material changes to existing sub-processors. The
                    School may object to a new sub-processor on reasonable data
                    protection grounds within 14 days of notification. If the
                    objection cannot be resolved, the School may terminate the
                    affected services without penalty.
                  </p>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='data-subject-rights' className='scroll-mt-24'>
                  <h2>9. Data Subject Rights</h2>
                  <p>
                    TEX will assist the School in responding to requests from
                    data subjects (including parents and eligible students under
                    FERPA) exercising their rights, including:
                  </p>
                  <ul>
                    <li>
                      Right of access / right to inspect and review (FERPA, GDPR
                      Article 15, APP 12)
                    </li>
                    <li>
                      Right to rectification / amendment (FERPA, GDPR Article
                      16, APP 13)
                    </li>
                    <li>Right to erasure / deletion (GDPR Article 17)</li>
                    <li>Right to data portability (GDPR Article 20)</li>
                    <li>
                      Right to restriction of processing (GDPR Article 18)
                    </li>
                    <li>Right to object to processing (GDPR Article 21)</li>
                    <li>Right to consent to disclosures (FERPA &sect;99.30)</li>
                  </ul>
                  <p>
                    The School is responsible for receiving and evaluating data
                    subject requests. TEX will respond to requests for
                    assistance within 5 business days and provide the necessary
                    data or system access to enable the School to fulfil its
                    obligations.
                  </p>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='data-return' className='scroll-mt-24'>
                  <h2>10. Data Return &amp; Deletion</h2>
                  <p>
                    Upon termination or expiry of the subscription agreement,
                    TEX will, at the School&apos;s written direction:
                  </p>
                  <ul>
                    <li>
                      <strong>Return:</strong> Export all School Data, including
                      Education Records, in a standard machine-readable format
                      (CSV/JSON) within 30 days of the termination date.
                    </li>
                    <li>
                      <strong>Delete:</strong> Securely delete all School Data
                      from TEX systems and sub-processor systems within 30 days
                      of the termination date, except where retention is
                      required by applicable law.
                    </li>
                    <li>
                      <strong>Certification:</strong> Upon request, provide
                      written certification that all School Data has been
                      deleted or returned in accordance with this section.
                    </li>
                  </ul>
                  <p>
                    During the 30-day post-termination window, the School may
                    access the Platform in read-only mode to verify data export
                    completeness.
                  </p>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='breach-notification' className='scroll-mt-24'>
                  <h2>11. Breach Notification</h2>
                  <p>In the event of a confirmed Data Breach, TEX will:</p>
                  <ul>
                    <li>
                      Notify the School without unreasonable delay and no later
                      than <strong>24 hours</strong> after becoming aware of the
                      breach.
                    </li>
                    <li>
                      Provide sufficient information for the School to assess
                      its notification obligations under FERPA, GDPR (Article
                      33/34), and/or the Australian NDB scheme.
                    </li>
                    <li>
                      Include in the notification: (a) nature and scope of the
                      breach, (b) categories and approximate number of affected
                      records, (c) likely consequences, (d) measures taken or
                      proposed to address the breach.
                    </li>
                    <li>
                      Cooperate fully with the School&apos;s investigation and
                      remediation efforts.
                    </li>
                    <li>
                      Provide follow-up reports as additional information
                      becomes available.
                    </li>
                  </ul>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='audit' className='scroll-mt-24'>
                  <h2>12. Audit Rights</h2>
                  <p>
                    The School has the right to audit TEX&apos;s compliance with
                    this DPA, subject to the following:
                  </p>
                  <ul>
                    <li>
                      Audits may be conducted no more than once per calendar
                      year, with at least 30 days&apos; advance written notice.
                    </li>
                    <li>
                      TEX will make available all information reasonably
                      necessary to demonstrate compliance, including security
                      documentation, processing records, and sub-processor
                      agreements.
                    </li>
                    <li>
                      Where a third-party audit or certification (e.g., SOC 2
                      Type II) is available, TEX may provide the audit report in
                      lieu of an on-site audit, provided the report addresses
                      the School&apos;s concerns.
                    </li>
                    <li>
                      The School may engage a qualified third-party auditor,
                      subject to reasonable confidentiality obligations.
                    </li>
                  </ul>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='term' className='scroll-mt-24'>
                  <h2>13. Term &amp; Termination</h2>
                  <p>
                    This DPA takes effect on the date the School begins using
                    the Platform and continues for the duration of the
                    subscription agreement. Obligations relating to data
                    security, confidentiality, data return, and deletion survive
                    termination.
                  </p>
                  <p>
                    Either party may terminate this DPA if the other party
                    materially breaches its obligations and fails to cure the
                    breach within 30 days of written notice. The School may
                    terminate immediately if TEX fails to comply with a
                    mandatory data protection obligation that cannot be cured.
                  </p>
                </section>
              </MotionSection>

              <hr />

              <MotionSection>
                <section id='contact' className='scroll-mt-24'>
                  <h2>14. Contact</h2>
                  <p>
                    To request a copy of this DPA for execution, or for
                    questions about data processing practices:
                  </p>
                  <p>
                    <strong>Privacy Officer:</strong>{' '}
                    <a
                      href='mailto:privacy@texedu.app'
                      className='text-primary hover:underline'
                    >
                      privacy@texedu.app
                    </a>
                    <br />
                    <strong>Legal enquiries:</strong>{' '}
                    <a
                      href='mailto:legal@texedu.app'
                      className='text-primary hover:underline'
                    >
                      legal@texedu.app
                    </a>
                    <br />
                    <strong>Mailing address:</strong> TEX Inc Pty Ltd, [Address
                    to be provided]
                  </p>
                </section>
              </MotionSection>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </>
  );
}
